import { OnboardingV2State } from '../../store/onboardingV2/types.ts';
import { hasWorkZonesStepCompleted } from './workZonesStep.ts';

export type OnboardingStep = 
  | 'accountType'
  | 'basicData'
  | 'skills'
  | 'workZones'
  | 'summary'
  | 'complete';

export interface StepInfo {
  id: OnboardingStep;
  label: string;
  isCompleted: boolean;
  canNavigate: boolean;
}

export const getCurrentStep = (state: OnboardingV2State): OnboardingStep => {
  if (!state.accountType) {
    return 'accountType';
  }

  const form = state.accountType === 'PERSON' ? state.personaForm : state.empresaForm;
  const hasBasicData = state.accountType === 'PERSON'
    ? !!(form.name && form.lastName)
    : !!form.companyName;

  if (!hasBasicData) {
    return 'basicData';
  }

  if (state.userSkills.length === 0 || state.preferredProfileCategories.length === 0) {
    return 'skills';
  }

  if (!hasWorkZonesStepCompleted(state)) {
    return 'workZones';
  }

  return 'complete';
};

export const getStepInfo = (step: OnboardingStep, state: OnboardingV2State): StepInfo => {
  const currentStep = getCurrentStep(state);
  const stepOrder: OnboardingStep[] = ['accountType', 'basicData', 'skills', 'workZones', 'summary', 'complete'];
  const currentIndex = stepOrder.indexOf(currentStep);
  const stepIndex = stepOrder.indexOf(step);

  let isCompleted = false;
  let canNavigate = false;

  switch (step) {
    case 'accountType':
      isCompleted = !!state.accountType;
      canNavigate = stepIndex <= currentIndex;
      break;
    case 'basicData':
      const form = state.accountType === 'PERSON' ? state.personaForm : state.empresaForm;
      isCompleted = state.accountType === 'PERSON'
        ? !!(form.name && form.lastName)
        : !!form.companyName;
      canNavigate = stepIndex <= currentIndex && !!state.accountType;
      break;
    case 'skills':
      isCompleted = state.userSkills.length > 0 && state.preferredProfileCategories.length > 0;
      canNavigate = stepIndex <= currentIndex && !!state.accountType;
      break;
    case 'workZones':
      isCompleted = hasWorkZonesStepCompleted(state);
      canNavigate = stepIndex <= currentIndex && !!state.accountType;
      break;
    case 'summary':
      isCompleted = currentStep === 'summary' || currentStep === 'complete';
      canNavigate = stepIndex <= currentIndex && !!state.accountType;
      break;
    case 'complete':
      isCompleted = currentStep === 'complete';
      canNavigate = false;
      break;
  }

  return {
    id: step,
    label: getStepLabel(step),
    isCompleted,
    canNavigate,
  };
};

export const getStepLabel = (step: OnboardingStep): string => {
  switch (step) {
    case 'accountType':
      return 'Tipo de cuenta';
    case 'basicData':
      return 'Datos básicos';
    case 'skills':
      return 'Habilidades';
    case 'workZones':
      return 'Zonas de trabajo';
    case 'summary':
      return 'Resumen';
    case 'complete':
      return 'Completado';
    default:
      return '';
  }
};

export const getAllSteps = (state: OnboardingV2State): StepInfo[] => {
  return [
    getStepInfo('accountType', state),
    getStepInfo('basicData', state),
    getStepInfo('skills', state),
    getStepInfo('workZones', state),
    getStepInfo('summary', state),
  ];
};

export const getPreviousStep = (currentStep: OnboardingStep): OnboardingStep | null => {
  const stepOrder: OnboardingStep[] = ['accountType', 'basicData', 'skills', 'workZones', 'summary', 'complete'];
  const currentIndex = stepOrder.indexOf(currentStep);
  
  if (currentIndex <= 0) {
    return null;
  }
  
  return stepOrder[currentIndex - 1];
};

